import React, { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAuth } from "pages/context/AuthContext";
import { useAlert } from "pages/context/AlertContext";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

const fmtDateTime = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleString("pt-PT", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function getEstadoContacto(row) {
  return Number(row?.contacto_realizado) === 1 ? "Realizado" : "Pendente";
}

export default function DialogClienteContactos({ open, onClose, item }) {
  const [loading, setLoading] = useState(false);
  const [contactos, setContactos] = useState([]);
  const { userData } = useAuth();
  const { showAlert } = useAlert();

  const [saving, setSaving] = useState(false);
  const [mostrarNovo, setMostrarNovo] = useState(false);
  const [mostrarEditar, setMostrarEditar] = useState(false);
  const [mostrarRealizar, setMostrarRealizar] = useState(false);
  const [openConfirmarRealizadoFuturo, setOpenConfirmarRealizadoFuturo] = useState(false);
  const [datacontactoRealizadoAjustada, setDatacontactoRealizadoAjustada] = useState("");

  const [novoContacto, setNovoContacto] = useState({
    datacontacto: "",
    observacoes: "",
  });
  const [editarContacto, setEditarContacto] = useState({
    id: 0,
    datacontacto: "",
    observacoes: "",
  });
  const [realizarContacto, setRealizarContacto] = useState({
    id: 0,
    datacontacto: "",
    observacoes: "",
  });

  const resetNovoContacto = () => {
    setNovoContacto({
      datacontacto: "",
      observacoes: "",
    });
    setMostrarNovo(false);
  };

  const resetEditarContacto = () => {
    setEditarContacto({
      id: 0,
      datacontacto: "",
      observacoes: "",
    });
    setMostrarEditar(false);
  };

  const resetRealizarContacto = () => {
    setRealizarContacto({
      id: 0,
      datacontacto: "",
      observacoes: "",
    });
    setMostrarRealizar(false);
  };

  const carregarContactos = async () => {
    if (!open || !item?.refcliente) {
      setContactos([]);
      return;
    }

    setLoading(true);

    const body = {
      refcliente: item?.refcliente || "",
      refpagador: item?.refpagador || "",
      refvendedor: item?.refvendedor || "",
    };

    const [payload, err] = await handleApi(api.post("Comercial/SelClienteContactos", body));

    setLoading(false);

    if (err) {
      setContactos([]);
      return;
    }

    const data = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
      ? payload.data
      : [];

    setContactos(data);
  };

  useEffect(() => {
    if (!open) return;
    carregarContactos();
  }, [open, item]);

  const contactoPendente = useMemo(
    () =>
      Array.isArray(contactos) ? contactos.find((x) => Number(x?.contacto_realizado) !== 1) : null,
    [contactos]
  );

  const handleEditarPendente = (contacto) => {
    if (!contacto) return;

    setMostrarNovo(false);
    setMostrarRealizar(false);
    setEditarContacto({
      id: Number(contacto?.id || 0),
      datacontacto: contacto?.datacontacto
        ? new Date(contacto.datacontacto).toISOString().slice(0, 16)
        : "",
      observacoes: contacto?.observacoes || "",
    });
    setMostrarEditar(true);
  };

  const handleMarcarRealizado = (contacto) => {
    if (!contacto) return;

    setMostrarNovo(false);
    setMostrarEditar(false);
    setRealizarContacto({
      id: Number(contacto?.id || 0),
      datacontacto: contacto?.datacontacto
        ? new Date(contacto.datacontacto).toISOString().slice(0, 16)
        : "",
      observacoes: contacto?.observacoes || "",
    });
    setMostrarRealizar(true);
  };

  const handleGuardarEdicao = async () => {
    const id = Number(editarContacto?.id || 0);
    const dt = (editarContacto?.datacontacto || "").trim();
    const obs = (editarContacto?.observacoes || "").trim();
    const idUtilizador = Number(userData?.id || 0);

    if (id <= 0) {
      showAlert("Contacto inválido.", "error");
      return;
    }

    if (!item?.refcliente) {
      showAlert("Cliente inválido.", "error");
      return;
    }

    if (!dt) {
      showAlert("Indica a data do contacto.", "warning");
      return;
    }

    if (idUtilizador <= 0) {
      showAlert("Não foi possível identificar o utilizador autenticado.", "error");
      return;
    }

    setSaving(true);

    const body = {
      id,
      refcliente: item?.refcliente || "",
      refpagador: item?.refpagador || "",
      refvendedor: item?.refvendedor || "",
      datacontacto: dt,
      contacto_realizado: 0,
      observacoes: obs,
      idutilizador: idUtilizador,
    };

    const [payload, err] = await handleApi(api.post("Comercial/UpdClienteContacto", body));

    setSaving(false);

    if (err) {
      showAlert(err?.message || "Erro ao atualizar o contacto.", "error");
      return;
    }

    showAlert(payload?.message || "Contacto atualizado com sucesso.", "success");

    resetEditarContacto();
    await carregarContactos();
  };

  const gravarRealizado = async (dataFinal) => {
    const id = Number(realizarContacto?.id || 0);
    const obs = (realizarContacto?.observacoes || "").trim();
    const idUtilizador = Number(userData?.id || 0);

    setSaving(true);

    const body = {
      id,
      refcliente: item?.refcliente || "",
      refpagador: item?.refpagador || "",
      refvendedor: item?.refvendedor || "",
      datacontacto: dataFinal,
      contacto_realizado: 1,
      observacoes: obs,
      idutilizador: idUtilizador,
    };

    const [payload, err] = await handleApi(api.post("Comercial/UpdClienteContacto", body));

    setSaving(false);

    if (err) {
      showAlert(err?.message || "Erro ao marcar o contacto como realizado.", "error");
      return;
    }

    showAlert(payload?.message || "Contacto marcado como realizado com sucesso.", "success");

    setOpenConfirmarRealizadoFuturo(false);
    setDatacontactoRealizadoAjustada("");
    resetRealizarContacto();
    await carregarContactos();
  };

  const handleGuardarRealizado = async () => {
    const id = Number(realizarContacto?.id || 0);
    const dt = (realizarContacto?.datacontacto || "").trim();
    const obs = (realizarContacto?.observacoes || "").trim();
    const idUtilizador = Number(userData?.id || 0);

    if (id <= 0) {
      showAlert("Contacto inválido.", "error");
      return;
    }

    if (!item?.refcliente) {
      showAlert("Cliente inválido.", "error");
      return;
    }

    if (!dt) {
      showAlert("Indica a data do contacto.", "warning");
      return;
    }

    if (!obs) {
      showAlert("As observações são obrigatórias ao marcar o contacto como realizado.", "warning");
      return;
    }

    if (idUtilizador <= 0) {
      showAlert("Não foi possível identificar o utilizador autenticado.", "error");
      return;
    }

    const dataEscolhida = new Date(dt);
    const agora = new Date();

    if (!Number.isNaN(dataEscolhida.getTime()) && dataEscolhida > agora) {
      const dataAgora = new Date().toISOString().slice(0, 16);

      setDatacontactoRealizadoAjustada(dataAgora);
      setOpenConfirmarRealizadoFuturo(true);
      return;
    }

    await gravarRealizado(dt);
  };

  const handleGuardarNovo = async () => {
    const dt = (novoContacto?.datacontacto || "").trim();
    const obs = (novoContacto?.observacoes || "").trim();
    const idUtilizador = Number(userData?.id || 0);

    if (!item?.refcliente) {
      showAlert("Cliente inválido.", "error");
      return;
    }

    if (!dt) {
      showAlert("Indica a data do contacto.", "warning");
      return;
    }

    if (idUtilizador <= 0) {
      showAlert("Não foi possível identificar o utilizador autenticado.", "error");
      return;
    }

    setSaving(true);

    const body = {
      refcliente: item?.refcliente || "",
      refpagador: item?.refpagador || "",
      refvendedor: item?.refvendedor || "",
      datacontacto: dt,
      contacto_realizado: 0,
      observacoes: obs,
      idutilizador: idUtilizador,
    };

    const [payload, err] = await handleApi(api.post("Comercial/InsClienteContacto", body));

    setSaving(false);

    if (err) {
      showAlert(err?.message || "Erro ao gravar o contacto.", "error");
      return;
    }

    showAlert(payload?.message || "Contacto registado com sucesso.", "success");

    resetNovoContacto();
    await carregarContactos();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <MDBox display="flex" flexDirection="column" gap={0.5}>
          <MDTypography variant="h6">Contactos do cliente</MDTypography>

          <MDTypography variant="button" color="text" display="block">
            Cliente: {item?.cliente || "-"} ({item?.refcliente || "-"})
          </MDTypography>

          <MDTypography variant="button" color="text" display="block">
            Pagador: {item?.pagador || "-"} ({item?.refpagador || "-"})
          </MDTypography>

          <MDTypography variant="caption" color="text" display="block">
            Vendedor: {item?.vendedor || "-"} ({item?.refvendedor || "-"})
          </MDTypography>
        </MDBox>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <MDBox py={4} display="flex" justifyContent="center">
            <CircularProgress size={26} />
          </MDBox>
        ) : (
          <MDBox display="flex" flexDirection="column" gap={2}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <MDTypography variant="button" fontWeight="bold">
                Estado atual
              </MDTypography>

              <Divider sx={{ my: 1 }} />

              {contactoPendente ? (
                <MDBox>
                  <MDTypography variant="caption" display="block">
                    Existe um contacto pendente agendado para:{" "}
                    <strong>{fmtDateTime(contactoPendente?.datacontacto)}</strong>
                  </MDTypography>

                  <MDTypography variant="caption" display="block">
                    Obs.: {contactoPendente?.observacoes || "-"}
                  </MDTypography>
                </MDBox>
              ) : (
                <MDTypography variant="caption" color="text">
                  Não existe contacto pendente.
                </MDTypography>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
              >
                <MDTypography variant="button" fontWeight="bold">
                  Histórico completo
                </MDTypography>

                {!contactoPendente ? (
                  <MDButton
                    color="success"
                    size="small"
                    onClick={() => {
                      setMostrarEditar(false);
                      setMostrarRealizar(false);
                      setMostrarNovo((prev) => !prev);
                    }}
                  >
                    {mostrarNovo ? "Cancelar novo contacto" : "Novo contacto"}
                  </MDButton>
                ) : null}
              </MDBox>

              <Divider sx={{ my: 1 }} />

              {mostrarNovo ? (
                <MDBox display="flex" flexDirection="column" gap={1.5} mb={2}>
                  <Paper variant="outlined" sx={{ p: 1.25 }}>
                    <MDBox display="flex" flexDirection="column" gap={1.5}>
                      <MDTypography variant="caption" color="text">
                        Se indicar uma data/hora no passado, o contacto será registado como
                        realizado e exigirá observações. Se indicar uma data futura, ficará
                        pendente.
                      </MDTypography>

                      <MDBox>
                        <label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>
                          Data do contacto
                        </label>
                        <TextField
                          type="datetime-local"
                          fullWidth
                          size="small"
                          value={novoContacto.datacontacto}
                          onChange={(e) =>
                            setNovoContacto((prev) => ({
                              ...prev,
                              datacontacto: e.target.value,
                            }))
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </MDBox>

                      <MDBox>
                        <label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>
                          Observações
                        </label>
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          minRows={3}
                          value={novoContacto.observacoes}
                          onChange={(e) =>
                            setNovoContacto((prev) => ({
                              ...prev,
                              observacoes: e.target.value,
                            }))
                          }
                        />
                      </MDBox>

                      <MDBox display="flex" justifyContent="flex-end" gap={1}>
                        <MDButton color="light" onClick={resetNovoContacto} disabled={saving}>
                          Cancelar
                        </MDButton>

                        <MDButton color="success" onClick={handleGuardarNovo} disabled={saving}>
                          {saving ? "A gravar..." : "Gravar contacto"}
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </Paper>
                </MDBox>
              ) : null}

              {!contactos.length ? (
                <MDTypography variant="caption" color="text">
                  Não existem contactos registados.
                </MDTypography>
              ) : (
                <MDBox display="flex" flexDirection="column" gap={1}>
                  {contactos.map((c, idx) => {
                    const pendente = Number(c?.contacto_realizado) !== 1;

                    return (
                      <Paper
                        key={`${c?.id || "contacto"}_${idx}`}
                        variant="outlined"
                        sx={{ p: 1.25 }}
                      >
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          gap={1}
                          flexWrap="wrap"
                        >
                          <MDBox>
                            <MDTypography variant="caption" display="block">
                              <strong>Data:</strong> {fmtDateTime(c?.datacontacto)}
                            </MDTypography>

                            <MDTypography variant="caption" display="block">
                              <strong>Utilizador:</strong> {c?.utilizador || "-"}
                            </MDTypography>

                            <MDTypography variant="caption" display="block">
                              <strong>Obs.:</strong> {c?.observacoes || "-"}
                            </MDTypography>
                          </MDBox>

                          <MDBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
                            {pendente ? (
                              <>
                                <MDButton
                                  color="info"
                                  size="small"
                                  onClick={() => handleEditarPendente(c)}
                                >
                                  Editar pendente
                                </MDButton>

                                <MDButton
                                  color="success"
                                  size="small"
                                  onClick={() => handleMarcarRealizado(c)}
                                >
                                  Marcar realizado
                                </MDButton>
                              </>
                            ) : (
                              <Chip
                                size="small"
                                label={getEstadoContacto(c)}
                                color="success"
                                variant="outlined"
                              />
                            )}
                          </MDBox>
                        </MDBox>

                        {mostrarEditar && Number(editarContacto?.id || 0) === Number(c?.id || 0) ? (
                          <MDBox mt={2} display="flex" flexDirection="column" gap={1.5}>
                            <Divider />

                            <MDBox>
                              <label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>
                                Data do contacto
                              </label>
                              <TextField
                                type="datetime-local"
                                fullWidth
                                size="small"
                                value={editarContacto.datacontacto}
                                onChange={(e) =>
                                  setEditarContacto((prev) => ({
                                    ...prev,
                                    datacontacto: e.target.value,
                                  }))
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </MDBox>

                            <MDBox>
                              <label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>
                                Observações
                              </label>
                              <TextField
                                fullWidth
                                size="small"
                                multiline
                                minRows={3}
                                value={editarContacto.observacoes}
                                onChange={(e) =>
                                  setEditarContacto((prev) => ({
                                    ...prev,
                                    observacoes: e.target.value,
                                  }))
                                }
                              />
                            </MDBox>

                            <MDBox display="flex" justifyContent="flex-end" gap={1}>
                              <MDButton
                                color="light"
                                onClick={resetEditarContacto}
                                disabled={saving}
                              >
                                Cancelar
                              </MDButton>

                              <MDButton
                                color="info"
                                onClick={handleGuardarEdicao}
                                disabled={saving}
                              >
                                {saving ? "A gravar..." : "Guardar alterações"}
                              </MDButton>
                            </MDBox>
                          </MDBox>
                        ) : null}

                        {mostrarRealizar &&
                        Number(realizarContacto?.id || 0) === Number(c?.id || 0) ? (
                          <MDBox mt={2} display="flex" flexDirection="column" gap={1.5}>
                            <Divider />

                            <MDTypography variant="caption" fontWeight="bold">
                              Marcar contacto como realizado
                            </MDTypography>

                            <MDBox>
                              <label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>
                                Data do contacto
                              </label>
                              <TextField
                                type="datetime-local"
                                fullWidth
                                size="small"
                                value={realizarContacto.datacontacto}
                                onChange={(e) =>
                                  setRealizarContacto((prev) => ({
                                    ...prev,
                                    datacontacto: e.target.value,
                                  }))
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </MDBox>

                            <MDBox>
                              <label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>
                                Observações
                              </label>
                              <TextField
                                fullWidth
                                size="small"
                                multiline
                                minRows={3}
                                value={realizarContacto.observacoes}
                                onChange={(e) =>
                                  setRealizarContacto((prev) => ({
                                    ...prev,
                                    observacoes: e.target.value,
                                  }))
                                }
                              />
                            </MDBox>

                            <MDBox display="flex" justifyContent="flex-end" gap={1}>
                              <MDButton
                                color="light"
                                onClick={resetRealizarContacto}
                                disabled={saving}
                              >
                                Cancelar
                              </MDButton>

                              <MDButton
                                color="success"
                                onClick={handleGuardarRealizado}
                                disabled={saving}
                              >
                                {saving ? "A gravar..." : "Confirmar realizado"}
                              </MDButton>
                            </MDBox>
                          </MDBox>
                        ) : null}
                      </Paper>
                    );
                  })}
                </MDBox>
              )}
            </Paper>
          </MDBox>
        )}
      </DialogContent>

      <DialogActions>
        <MDButton onClick={onClose} color="light">
          Fechar
        </MDButton>
      </DialogActions>

      <Dialog
        open={openConfirmarRealizadoFuturo}
        onClose={() => {
          setOpenConfirmarRealizadoFuturo(false);
          setDatacontactoRealizadoAjustada("");
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Confirmar ajuste de data</DialogTitle>

        <DialogContent dividers>
          <MDTypography variant="button" color="text">
            A data/hora indicada é futura. Pretende antecipá-la para a data/hora atual?
          </MDTypography>
        </DialogContent>

        <DialogActions>
          <MDButton
            color="light"
            onClick={() => {
              setOpenConfirmarRealizadoFuturo(false);
              setDatacontactoRealizadoAjustada("");
            }}
          >
            Não
          </MDButton>

          <MDButton
            color="success"
            onClick={async () => {
              setRealizarContacto((prev) => ({
                ...prev,
                datacontacto: datacontactoRealizadoAjustada,
              }));

              await gravarRealizado(datacontactoRealizadoAjustada);
            }}
          >
            Sim
          </MDButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
